// React Imports
import { useMemo } from 'react'

// MUI Imports
import { deepmerge } from '@mui/utils'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'
import CssBaseline from '@mui/material/CssBaseline'
import type {} from '@mui/material/themeCssVarsAugmentation' //! Do not remove this import otherwise you will get type errors while making a production build
import type {} from '@mui/lab/themeAugmentation' //! Do not remove this import otherwise you will get type errors while making a production build

// Third-party Imports
import { useMedia } from 'react-use'

// Type Imports
import type { ChildrenType, Direction, SystemMode } from '@core/types'

// Component Imports
import ModeChanger from './mode-changer'

// Config Imports
import themeSettings, { modeStorageKey } from '@configs/theme'

// Hook Imports
import { useSettings } from '@/stores/settings'

// Core Theme Imports
import defaultCoreTheme from '@core/theme'
import { paletteOf, mainColor } from '@core/theme/palette'

type Props = ChildrenType & {
  direction: Direction
  systemMode: SystemMode
}

const CustomThemeProvider = (props: Props) => {
  // Props
  const { children, direction, systemMode } = props

  // Hooks
  const { settings } = useSettings()
  const isDark = useMedia('(prefers-color-scheme: dark)', systemMode === 'dark')

  // Vars — in a SPA there is always a window, so the mode comes straight from
  // the settings.
  const currentMode: SystemMode =
    settings.mode === 'system' ? (isDark ? 'dark' : 'light') : ((settings.mode as SystemMode) ?? systemMode)

  // Merge the primary color scheme override with the core theme
  const theme = useMemo(() => {
    /*
     * The theme primary already arrives built from `@core/theme`; what lands
     * here is only the case where the user picked another color in the
     * Customizer — and even then the tones come from the same function, so
     * there are never two derivation rules.
     */
    const chosen = settings.primaryColor
    const picked = !chosen || chosen === mainColor(themeSettings.colors.primary) ? themeSettings.colors.primary : chosen

    // Each scheme derives its own tones: the readable tone of a color depends
    // on the paper it will be read on, so light and dark cannot share one.
    const newTheme = {
      colorSchemes: {
        light: { palette: { primary: paletteOf(picked, 'primary', themeSettings.surfaces.light.paper) } },
        dark: { palette: { primary: paletteOf(picked, 'primary', themeSettings.surfaces.dark.paper) } }
      },
      cssVariables: {
        colorSchemeSelector: 'data'
      }
    }

    const coreTheme = deepmerge(defaultCoreTheme(settings, currentMode, direction), newTheme)

    return createTheme(coreTheme)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.primaryColor, settings.skin, currentMode])

  // Replaces Next's AppRouterCacheProvider: same Emotion cache setup
  // (prepend: true puts MUI's CSS before Tailwind's).
  const emotionCache = useMemo(() => createCache({ key: 'mui', prepend: true }), [])

  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={theme} defaultMode={systemMode} modeStorageKey={modeStorageKey}>
        <>
          <ModeChanger systemMode={systemMode} />
          <CssBaseline />
          {children}
        </>
      </ThemeProvider>
    </CacheProvider>
  )
}

export default CustomThemeProvider
